import { useState, useEffect } from 'react';
import { generateInvoicePDF } from '../../../../utils/pdfGenerator';

// ============================================================================
// Custom Hooks - Extracted Business Logic
// ============================================================================

/**
 * useInvoiceListData - Manages invoice list state and API data synchronization
 * @param {Array} apiData - Invoice data from API
 * @returns {Object} { invoiceListData, setInvoiceListData }
 */
export const useInvoiceListData = (apiData, mockData) => {
    const [invoiceListData, setInvoiceListData] = useState(apiData || mockData);

    useEffect(() => {
        if (Array.isArray(apiData)) {
            setInvoiceListData(apiData);
        } else if (!apiData) {
            setInvoiceListData(mockData);
        }
    }, [apiData, mockData]);

    return { invoiceListData, setInvoiceListData };
};

/**
 * useInvoiceDetailsPopup - Manages invoice details popup state and handlers
 * @returns {Object} { showInvoiceDetailsPopup, selectedInvoice, handleViewInvoice, handleCloseInvoicePopup }
 */
export const useInvoiceDetailsPopup = () => {
    const [showInvoiceDetailsPopup, setShowInvoiceDetailsPopup] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    const handleViewInvoice = invoice => {
        console.log('View invoice:', invoice);
        setSelectedInvoice(invoice);
        setShowInvoiceDetailsPopup(true);
    };

    const handleCloseInvoicePopup = () => {
        setShowInvoiceDetailsPopup(false);
        setSelectedInvoice(null);
    };

    return { showInvoiceDetailsPopup, selectedInvoice, handleViewInvoice, handleCloseInvoicePopup };
};

/**
 * useWhatsAppPopup - Manages WhatsApp popup state and messaging logic
 * @returns {Object} { showWhatsAppPopup, selectedInvoiceForWhatsApp, handleWhatsApp, closeWhatsAppPopup, handleOpenWhatsApp }
 */
export const useWhatsAppPopup = () => {
    const [showWhatsAppPopup, setShowWhatsAppPopup] = useState(false);
    const [selectedInvoiceForWhatsApp, setSelectedInvoiceForWhatsApp] = useState(null);

    const handleWhatsApp = invoice => {
        console.log('WhatsApp:', invoice);
        setSelectedInvoiceForWhatsApp(invoice);
        setShowWhatsAppPopup(true);
    };

    const closeWhatsAppPopup = () => {
        setShowWhatsAppPopup(false);
        setSelectedInvoiceForWhatsApp(null);
    };

    const handleOpenWhatsApp = invoice => {
        const message = `Hello ${invoice.companyName},\n\nYour invoice from 3PLVision WMS is ready:\n\nInvoice: ${invoice.invoiceNumber}\nDate: ${invoice.invoiceDate}\nDue: ${invoice.dueDate}\nAmount Due: $${Math.abs(invoice.amount).toFixed(2)}\n\nDownload: https://portal.3plvision.com/invoices/${invoice.invoiceNumber}.pdf\n\nFor queries: billing@3plvision.com\n\n3PLVision Team`;
        window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
        closeWhatsAppPopup();
    };

    return { showWhatsAppPopup, selectedInvoiceForWhatsApp, handleWhatsApp, closeWhatsAppPopup, handleOpenWhatsApp };
};

/**
 * useInvoiceActions - Manages all invoice action handlers and routing
 * @param {Object} filterData - Current filter/sort state
 * @param {Function} setfilterData - Function to update filter state
 * @returns {Object} { handleTableAction, handleSortingColumn, ...actionHandlers }
 */
export const useInvoiceActions = (filterData, setfilterData) => {
    const handleSortingColumn = key => {
        let newSortOrder = filterData.sortOrder === 'ASCENDING' ? 'DESCENDING' : 'ASCENDING';
        setfilterData({
            ...filterData,
            sortColumn: key,
            sortOrder: newSortOrder,
        });
    };

    const handleDownloadPDF = invoice => {
        const invoiceElement = document.getElementById('invoice-pdf-content');
        if (!invoiceElement) {
            console.error('Invoice PDF content element not found');
            alert('Unable to generate PDF. Invoice content not found.');
            return;
        }
        try {
            const filename = invoice.invoiceNumber ? `${invoice.invoiceNumber}.pdf` : 'invoice.pdf';
            generateInvoicePDF(invoiceElement, filename);
            console.log(`PDF generated: ${filename}`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Failed to download invoice PDF. Please try again.');
        }
    };

    const handleResendEmail = invoice => {
        console.log('Resend email:', invoice);
    };

    const handleVoidInvoice = invoice => {
        console.log('Void invoice:', invoice);
    };

    const handlePayNow = invoice => {
        console.log('Pay Now:', invoice);
    };

    // Wrapper function to route DataTable action calls to specific handlers
    const handleTableAction = (invoice, actionType, handlers) => {
        switch (actionType) {
            case 'view':
                handlers.handleViewInvoice(invoice);
                break;
            case 'download':
                handleDownloadPDF(invoice);
                break;
            case 'whatsapp':
                handlers.handleWhatsApp(invoice);
                break;
            case 'resendEmail':
                handleResendEmail(invoice);
                break;
            case 'voidInvoice':
                handleVoidInvoice(invoice);
                break;
            case 'payNow':
                handlePayNow(invoice);
                break;
            default:
                console.warn('Unknown action type:', actionType);
        }
    };

    return { handleTableAction, handleSortingColumn, handleDownloadPDF, handleResendEmail, handleVoidInvoice, handlePayNow };
};
